<script setup>
	import { ref, computed, onMounted, watch } from 'vue';
	import { useStore } from 'vuex';
	import { DsText, DsButton, dsUseIsMobile } from '@coloplast/design-system';
	import { clearQueryStringParameters } from 'shared/services/windowLocationService';
	import { check_tool } from 'shared/services/tracking/toolService';

	const store = useStore();
	const setReturnButtonText = (payload) => store.commit('setReturnButtonText', payload);

	const { isMobile } = dsUseIsMobile();

	const props = defineProps({
		vm: {
			type: Object,
			default: () => {},
		},
	});

	const isToolStarted = ref(false);
	const activeQuestion = ref(null);

	const handleCheckToolTracking = (stateValue) => {
		check_tool({
			event: 'check_tool',
			check_id: 'fit_tool',
			check_name: 'Fit Tool',
			check_question_id: '1',
			check_question_name: 'fit tool question',
			check_question_state: stateValue,
		});
	};

	const startTheTool = () => {
		isToolStarted.value = true;
		activeQuestion.value = 0;

		if (!isResultShown.value) {
			handleCheckToolTracking('start');
		}
	};

	const setActiveQuestion = (buttonValue, currentIndex) => {
		if (buttonValue === 'next') {
			activeQuestion.value = currentIndex + 1;
		} else {
			activeQuestion.value = currentIndex - 1;
		}
	};

	const result = ref({
		shape: +sessionStorage.getItem('shapeValue'),
		positioning: +sessionStorage.getItem('positioningValue'),
		folds: +sessionStorage.getItem('foldsValue'),
	});

	const isQuestionAnswered = computed(() => {
		let tempQuestion;

		switch (activeQuestion.value) {
			case 0:
				tempQuestion = !!result.value.shape;
				break;
			case 1:
				tempQuestion = !!result.value.positioning
				break;
			case 2:
				tempQuestion = !!result.value.folds
				break;
		}

		return tempQuestion;
	});

	const mobileImage = ref(props.vm.BaseViewModel.DefaultMobileImageUrl);
	const desktopImage = ref(props.vm.BaseViewModel.DefaultDesktopImageUrl);

	const setAnswers = () => {
		if (isResultReady.value) {
			for (const property in result.value) {
				questionList.value.forEach(questionItem => {
					if (questionItem.id === property) {
						questionItem.options.forEach(optionItem => optionItem.chosen = optionItem.id === result.value[property]);
					}
				})
			}
		}
	};

	const setResultValue = (selectedOptionName, selectedOptionId, selectedQuestion) => {
		if (!isMobile.value) {
			activeQuestion.value = questionList.value.indexOf(selectedQuestion);
		}

		switch (activeQuestion.value) {
			case 0:
				result.value.shape = selectedOptionId;
				break;
			case 1:
				result.value.positioning = selectedOptionId;
				break;
			case 2:
				result.value.folds = selectedOptionId;
				break;
		}

		questionList.value[activeQuestion.value].options.forEach(item => item.chosen = item.name === selectedOptionName);
	};

	const showResults = () => {
		sessionStorage.setItem('shapeValue', result.value.shape);
		sessionStorage.setItem('positioningValue', result.value.positioning);
		sessionStorage.setItem('foldsValue', result.value.folds);

		handleCheckToolTracking('finish');

		const url = new URL(window.location.href);
		const params = `?${new URLSearchParams(result.value).toString()}`;
		window.history.pushState({}, '', url + params);
		window.location.reload();
	};

	const isResultReady = computed(() => {
		return Object.values(result.value).every(value => value !== 0);
	});

	const isPartialResultReady = computed(() => {
		return Object.values(result.value).some(value => value !== 0);
	});

	const isResultShown = computed(() => {
		return props.vm.BaseViewModel.ShowResult;
	});

	const progressBarPercentage = computed(() => {
		switch (activeQuestion.value) {
			case 0:
				return 100 / 3;
			case 1:
				return 100 / 3 * 2;
			case 2:
				return 100;
			default:
				return 0;
		}
	});

	const setImage = () => {
		let chosenProduct = props.vm.BaseViewModel.Images.find(chosenProduct =>
			chosenProduct.ShapeValue === result.value.shape &&
			chosenProduct.PositioningValue === result.value.positioning &&
			chosenProduct.FoldsValue === result.value.folds
		);

		mobileImage.value = chosenProduct?.MobileImageUrl ?? props.vm.BaseViewModel.DefaultMobileImageUrl;
		desktopImage.value = chosenProduct?.DesktopImageUrl ?? props.vm.BaseViewModel.DefaultDesktopImageUrl;
	};

	const scrollToTop = () => {
		setTimeout(() => {
			const currentScrollPosition = document.documentElement.scrollTop || document.body.scrollTop || window.scrollY;

			if (currentScrollPosition > 0) {
				window.scrollTo(0, 0);
			}
		}, 200);
	};

	const questionList = ref([
		{
			id: 'shape',
			text: props.vm.CurrentBlock.ShapeQuestionText,
			options: [
				{
					id: 1,
					name: props.vm.CurrentBlock.RegularShapeOptionText,
					chosen: false,
				},
				{
					id: 2,
					name: props.vm.CurrentBlock.InwardsShapeOptionText,
					chosen: false,
				},
				{
					id: 3,
					name: props.vm.CurrentBlock.OutwardShapeOptionText,
					chosen: false,
				},
			],
		},
		{
			id: 'positioning',
			text: props.vm.CurrentBlock.PositioningQuestionText,
			options: [
				{
					id: 1,
					name: props.vm.CurrentBlock.SameLevelOptionText,
					chosen: false,
				},
				{
					id: 2,
					name: props.vm.CurrentBlock.BelowSkinOptionText,
					chosen: false,
				},
				{
					id: 3,
					name: props.vm.CurrentBlock.AboveSkinOptionText,
					chosen: false,
				},
			],
		},
		{
			id: 'folds',
			text: props.vm.CurrentBlock.FoldsQuestionText,
			options: [
				{
					id: 1,
					name: props.vm.CurrentBlock.NoFoldsOptionText,
					chosen: false,
				},
				{
					id: 2,
					name: props.vm.CurrentBlock.DeepFoldsOptionText,
					chosen: false,
				},
			],
		},
	]);

	onMounted(() => {
		if (isResultShown.value) {
			scrollToTop();
		}

		if (!isResultShown.value) {
			clearQueryStringParameters(window.location.href);
		}

		if (isResultReady.value) {
			startTheTool();
			setAnswers();
			setImage();
		}

		setReturnButtonText(props.vm.CurrentBlock.BackToTheToolButtonText);
	});

	watch(
		() => result.value,
		() => {
			if (isPartialResultReady.value) {
				setImage();
			}
		}, { deep: true }
	);
</script>

<template>
	<div
		v-if="!isResultShown"
		class="c-fit-tool"
	>
		<Transition
			appear
			name="fit-tool"
			mode="out-in"
		>
			<section
				v-if="!isToolStarted"
				class="c-fit-tool__introduction-wrapper"
			>
				<div class="c-fit-tool__introduction">
                    <slot name="introduction"></slot>
					<div
						v-if="vm.CurrentBlock.StartToolButtonText"
						class="c-fit-tool__introduction-button"
					>
						<DsButton
							type="button"
							variant="primary"
							:disabled="null"
							:click-handler="startTheTool"
							data-testid="start-tool"
						>
							{{ vm.CurrentBlock.StartToolButtonText }}
						</DsButton>
					</div>
				</div>
			</section>
			<section
				v-else-if="isToolStarted && isMobile"
				class="c-fit-tool__questionnaire-wrapper"
			>
				<div class="c-fit-tool__questionnaire">
                    <slot name="questionnaire"></slot>
					<img
						:src="`${mobileImage}?im=Resize=(296,156)`"
						:alt="vm.CurrentBlock.IntroductionHeadline"
						loading="lazy"
						class="c-fit-tool__questionnaire-image"
					>
					<progress
						:value="progressBarPercentage"
						max="100"
						class="c-fit-tool__questionnaire-progressbar"
					>
					</progress>
					<template v-if="questionList.length">
						<template
							v-for="(question, index) in questionList"
							:key="question.text"
						>
							<div
								v-if="activeQuestion === index"
								class="c-fit-tool__questionnaire-list"
							>
								<DsText
									html-element="p"
									text-style="regular"
									text-type="body-md"
									class="c-fit-tool__questionnaire-list-description"
								>
									{{ question.text }}
								</DsText>
								<div class="c-fit-tool__questionnaire-list-item">
									<button
										v-for="option in question.options"
										:key="option.name"
										type="button"
										class="c-fit-tool__questionnaire-list-button"
										:class="{ 'c-fit-tool__questionnaire-list-button--chosen' : option.chosen }"
										@click="setResultValue(option.name, option.id, question)"
									>
										<DsText
											html-element="span"
											text-style="medium"
											text-type="body-lg"
										>
											{{ option.name }}
										</DsText>
									</button>
								</div>
								<div
									v-if="vm.CurrentBlock.SearchProductsButtonText || vm.CurrentBlock.NextButtonText || vm.CurrentBlock.PreviousButtonText"
									class="c-fit-tool__questionnaire-navigation"
								>
									<DsButton
										v-if="vm.CurrentBlock.SearchProductsButtonText && activeQuestion === 2"
										type="button"
										variant="primary"
										:disabled="!isResultReady"
										:click-handler="showResults"
									>
										{{ vm.CurrentBlock.SearchProductsButtonText }}
									</DsButton>
									<DsButton
										v-if="vm.CurrentBlock.NextButtonText && activeQuestion !== 2"
										type="button"
										variant="primary"
										:disabled="!isQuestionAnswered"
										:click-handler="() => setActiveQuestion('next', index)"
									>
										{{ vm.CurrentBlock.NextButtonText }}
									</DsButton>
									<DsButton
										v-if="vm.CurrentBlock.PreviousButtonText"
										type="button"
										:disabled="activeQuestion === 0"
										:click-handler="() => setActiveQuestion('previous', index)"
									>
										{{ vm.CurrentBlock.PreviousButtonText }}
									</DsButton>
								</div>
							</div>
						</template>
					</template>
				</div>
			</section>
			<section
				v-else-if="isToolStarted && !isMobile"
				class="c-fit-tool__questionnaire-container"
			>
				<div class="c-fit-tool__questionnaire-wrapper c-fit-tool__questionnaire-wrapper--desktop">
					<div class="c-fit-tool__questionnaire">
						<slot name="questionnaire"></slot>
						<div
							v-if="questionList.length"
							class="c-fit-tool__questionnaire-list-wrapper"
						>
							<div v-for="question in questionList"
								 :key="question.text"
								 class="c-fit-tool__questionnaire-list c-fit-tool__questionnaire-list--desktop"
							>
								<DsText
									html-element="p"
									text-style="regular"
									text-type="body-md"
									class="c-fit-tool__questionnaire-list-description"
								>
									{{ question.text }}
								</DsText>
								<div class="c-fit-tool__questionnaire-list-item c-fit-tool__questionnaire-list-item--desktop">
									<button
										v-for="option in question.options"
										:key="option.name"
										type="button"
										class="c-fit-tool__questionnaire-list-button"
										:class="{ 'c-fit-tool__questionnaire-list-button--chosen' : option.chosen }"
										@click="setResultValue(option.name, option.id, question)"
									>
										<DsText
											html-element="span"
											text-style="medium"
											text-type="body-md"
										>
											{{ option.name }}
										</DsText>
									</button>
								</div>
							</div>
							<div
								v-if="vm.CurrentBlock.SearchProductsButtonText"
								class="c-fit-tool__questionnaire-navigation c-fit-tool__questionnaire-navigation--desktop"
							>
								<DsButton
									v-if="vm.CurrentBlock.SearchProductsButtonText"
									type="button"
									variant="primary"
									:disabled="!isResultReady"
									:click-handler="showResults"
								>
									{{ vm.CurrentBlock.SearchProductsButtonText }}
								</DsButton>
							</div>
						</div>
					</div>
				</div>
				<img
					:src="`${desktopImage}?im=Resize=(782,516)`"
					:alt="vm.CurrentBlock.IntroductionHeadline"
					loading="lazy"
					class="c-fit-tool__questionnaire-image c-fit-tool__questionnaire-image--desktop"
				>
			</section>
		</Transition>
	</div>
	<slot></slot>
</template>
